<template>
  <v-dialog v-model="showCreateDockerView" width="70vw" persistent>
    <v-card :loading="loading">
      <v-card-title>
        <span class="text-h6"
          >Container
          <v-chip small color="primary"
            >{{ update ? 'Update' : 'New' }}
          </v-chip></span
        >
        <v-spacer></v-spacer>
        <v-btn @click="cancel" icon>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form
          lazy-validation
          v-model="form"
          ref="newDocker"
          :disabled="loading"
        >
          <v-row>
            <v-col cols="12">
              <v-alert
                border="left"
                colored-border
                type="info"
                class="pb-0"
                transition="fade-transition"
              >
                <span>
                  Enter the first part of the address of the container you want
                  to add without any Tag information.
                </span>
                <v-text-field
                  v-model="updatedDocker.url"
                  label="URL of new container"
                  class="mt-3"
                  outlined
                  prepend-icon="mdi-link"
                  clearable
                  persistent-hint
                  hint="This URL will be used to download the container image."
                >
                </v-text-field>
              </v-alert>
            </v-col>
            <v-row class="pt-0 h-100">
              <v-col :cols="!hasChildren ? 12 : 12" class="pa-2 ps-4">
                <v-row>
                  <v-col cols="6" class="pa-2">
                    <v-text-field
                      v-model="updatedDocker.docker_name"
                      label="*Name of your new container"
                      :rules="rules"
                      clearable
                      outlined
                      persistent-hint
                      hint="This name will be used to identify the container."
                    />
                  </v-col>

                  <v-col cols="6" class="pa-2">
                    <v-select
                      v-model="updatedDocker.type"
                      label="*Container type"
                      :rules="rules"
                      :items="types"
                      outlined
                      clearable
                      persistent-hint
                      hint="This type will be used to categorize the container."
                    />
                  </v-col>

                  <v-col cols="6" class="pa-2">
                    <v-combobox
                      v-model="updatedDocker.labels"
                      label="Labels about new container"
                      outlined
                      multiple
                      persistent-hint
                      clearable
                      deletable-chips
                      small-chips
                      chips
                      hint="These labels will be used to provide better search results. (Click enter to add a new label)"
                    />
                  </v-col>

                  <v-col cols="6" class="pa-2">
                    <v-select
                      v-model="updatedDocker.source"
                      label="*Sources of your container"
                      :rules="rules"
                      :items="allSources"
                      hide-details="auto"
                      outlined
                      item-text="source_name"
                      return-object
                      small-chips
                      deletable-chips
                      chips
                      persistent-hint
                      hint="This source will be used to categorize the container."
                    />
                  </v-col>

                  <v-col cols="6" class="pa-2">
                    <v-select
                      v-model="selectedEngine"
                      label="Select an engine (Optional)"
                      :items="engines"
                      item-text="name"
                      return-object
                      hide-details="auto"
                      outlined
                      small-chips
                      deletable-chips
                      chips
                      persistent-hint
                      hint="This engine will be used to categorize the container."
                    />
                  </v-col>

                  <v-col cols="6" class="pa-2">
                    <v-text-field
                      v-model="updatedDocker.short_desc"
                      class="mt-1"
                      clearable
                      label="Short description"
                      outlined
                    />
                  </v-col>

                  <v-col cols="6" class="pa-2">
                    <v-file-input
                      @change="(file) => saveDockerImage(file, 'background')"
                      :rules="fileRules"
                      accept="image/png, image/jpeg, image/bmp"
                      outlined
                      label="Background Image"
                      :prepend-icon="null"
                    />
                  </v-col>
                  <v-col cols="6" class="pa-2">
                    <v-file-input
                      @change="(file) => saveDockerImage(file, 'cover')"
                      :rules="fileRules"
                      accept="image/png, image/jpeg, image/bmp"
                      outlined
                      label="Cover Image"
                      :prepend-icon="null"
                    />
                  </v-col>
                  <v-col cols="12" class="pa-2">
                    <v-combobox
                      v-model="updatedDocker.execute_command"
                      label="Execute Command about container"
                      outlined
                      multiple
                      persistent-hint
                      clearable
                      deletable-chips
                      small-chips
                      chips
                    />
                  </v-col>
                  <v-col :cols="2" class="pa-2 ps-4">
                    <v-switch
                      v-model="updatedDocker.background_is_dark"
                      label="Background is dark"
                      class="mt-1"
                    />
                  </v-col>
                  <v-col :cols="2">
                    <v-switch
                      v-model="updatedDocker.cover_is_dark"
                      label="Cover is dark"
                      class="mt-1"
                    />
                  </v-col>
                  <v-col :cols="2">
                    <v-switch
                      v-model="updatedDocker.check_gpu"
                      label="Check GPU"
                      class="mt-1"
                    />
                  </v-col>
                  <v-col :cols="2">
                    <v-switch
                      v-model="updatedDocker.check_compute_capability"
                      label="Check Compute Capability"
                      class="mt-1"
                    />
                  </v-col>
                  <v-col :cols="2">
                    <v-switch
                      v-model="updatedDocker.check_jetpack_version"
                      label="Check Jetpack Version"
                      class="mt-1"
                    />
                  </v-col>
                  <v-col :cols="2">
                    <v-switch
                      v-model="updatedDocker.is_spark"
                      label="Is Spark"
                      class="mt-1"
                    />
                  </v-col>
                  </v-row>
                  <!-- Model Selection Row (if engine exists) -->
                  <v-row v-if="hasConfigs">
                    <v-col cols="4" class="mt-0 pt-0 ps-0 h-100">
                      <model-tags
                        :engineId="selectedEngine ? selectedEngine.id : null"
                        @update:selected="selectedTag = $event"
                        :selectedTag="
                          selectedModelInfo ? this.selectedModelInfo.tag_id : null
                        "
                      />
                    </v-col>
                    <v-col cols="4" class="mt-0 pt-0 ps-0 h-100">
                      <v-autocomplete
                        class="custom-autocomplete"
                        v-model="selectedTags"
                        :items="availableTagsForSelection"
                        item-text="name"
                        item-value="id"
                        label="Select multiple tags to update"
                        outlined
                        hide-details
                        clearable
                        multiple
                        small-chips
                        persistent-hint
                        return-object
                        :item-disabled="(item) => item.disabled"
                      >
                        <template v-slot:prepend-item>
                          <v-list-item ripple @click="toggleSelectAll">
                            <v-list-item-action>
                              <v-icon :color="selectedTags.length > 0 ? 'primary' : ''">
                                {{ selectAllIcon }}
                              </v-icon>
                            </v-list-item-action>
                            <v-list-item-content>
                              <v-list-item-title>
                                {{ isAllSelected ? 'Deselect All' : 'Select All' }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-divider class="mt-2"></v-divider>
                        </template>
                        <template v-slot:selection="{ index }">
                          <template v-if="index === 0">
                            <span>
                              {{
                                isAllSelected
                                  ? 'All tags selected'
                                  : `${selectedTags.length} version tag${selectedTags.length > 1 ? 's' : ''} selected`
                              }}
                            </span>
                          </template>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
              </v-col>
            </v-row>

            <v-col cols="12" class="ms-0 mb-0 pb-0">
              <!-- Configuration Tabs -->
              <v-tabs v-model="configTab" background-color="primary" dark class="mb-3">
                <v-tab key="app-x86">
                  <v-icon left>mdi-desktop-classic</v-icon>
                  Application x86
                </v-tab>
                <v-tab key="app-jetson">
                  <v-icon left>mdi-chip</v-icon>
                  Application Jetson
                </v-tab>
                <v-tab key="model-x86" v-if="hasConfigs && this.selectedTag" :disabled="!this.selectedTag">
                  <v-icon left>mdi-cube-outline</v-icon>
                  Model Tag x86
                </v-tab>
                <v-tab key="model-jetson" v-if="hasConfigs && this.selectedTag" :disabled="!this.selectedTag">
                  <v-icon left>mdi-cube</v-icon>
                  Model Tag Jetson
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="configTab">
                <!-- Application x86 Settings -->
                <v-tab-item key="app-x86">
                  <v-expansion-panels>
                    <v-expansion-panel style="border: 2px solid black" class="mb-2">
                      <v-expansion-panel-header>
                        Container Options (x86)
                        <small style="font-size: 0.7rem; margin-left: 5px">
                          Docker options for x86 architecture.
                        </small>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <create-docker-options
                          :dockerOptions="this.updatedDocker.docker_options"
                          :dialog="showNewDockerOptions"
                          :opts="dockerOpts"
                          :isValid="this.isOptsValidAppX86"
                          @update:isValid="isOptsValidAppX86 = $event"
                        ></create-docker-options>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel style="border: 2px solid black" class="mb-2">
                      <v-expansion-panel-header>
                        Environment Variables (x86)
                        <small style="font-size: 0.7rem; margin-left: 5px">
                          Environment variables for x86 architecture.
                        </small>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row class="d-flex justify-end">
                          <v-col cols="4" class="ma-0">
                            <v-autocomplete
                              outlined
                              hide-details
                              v-model="selectedEnvVariableContainerOptionAppX86"
                              :items="containerOptions.filter((item) => item.type === 'env')"
                              item-text="name"
                              return-object
                              label="Choose Environment Options"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <create-docker-options
                            :dockerOptions="this.updatedDocker.env_keys"
                            :dialog="showNewDockerOptions"
                            :opts="this.selectedEnvVariableContainerOptionAppX86?.options"
                            :isValid="this.isEnvValidAppX86"
                            @update:isValid="isEnvValidAppX86 = $event"
                          ></create-docker-options>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel style="border: 2px solid black" class="mb-2">
                      <v-expansion-panel-header>
                        {{ updatedDocker.docker_name }} Arguments (x86)
                        <small style="font-size: 0.7rem; margin-left: 5px">
                          Engine arguments for x86 architecture.
                        </small>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row class="d-flex justify-end">
                          <v-col cols="4" class="ma-0">
                            <v-autocomplete
                              outlined
                              hide-details
                              v-model="selectedEngineArgContainerOptionAppX86"
                              :items="containerOptions.filter((item) => item.type === 'arg')"
                              item-text="name"
                              return-object
                              label="Choose Argument Options"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <create-docker-options
                            :dockerOptions="this.updatedDocker.arguments"
                            :dialog="showNewDockerOptions"
                            :opts="this.selectedEngineArgContainerOptionAppX86?.options"
                            :isValid="this.isArgValidAppX86"
                            @update:isValid="isArgValidAppX86 = $event"
                          ></create-docker-options>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-tab-item>

                <!-- Application Jetson Settings -->
                <v-tab-item key="app-jetson">
                  <v-expansion-panels>
                    <v-expansion-panel style="border: 2px solid black" class="mb-2">
                      <v-expansion-panel-header>
                        Container Options (Jetson)
                        <small style="font-size: 0.7rem; margin-left: 5px">
                          Docker options for Jetson architecture.
                        </small>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <create-docker-options
                          :dockerOptions="this.updatedDocker.jetson_docker_options"
                          :dialog="showNewDockerOptions"
                          :opts="dockerOpts"
                          :isValid="this.isOptsValidAppJetson"
                          @update:isValid="isOptsValidAppJetson = $event"
                        ></create-docker-options>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel style="border: 2px solid black" class="mb-2">
                      <v-expansion-panel-header>
                        Environment Variables (Jetson)
                        <small style="font-size: 0.7rem; margin-left: 5px">
                          Environment variables for Jetson architecture.
                        </small>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row class="d-flex justify-end">
                          <v-col cols="4" class="ma-0">
                            <v-autocomplete
                              outlined
                              hide-details
                              v-model="selectedEnvVariableContainerOptionAppJetson"
                              :items="containerOptions.filter((item) => item.type === 'env')"
                              item-text="name"
                              return-object
                              label="Choose Environment Options"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <create-docker-options
                            :dockerOptions="this.updatedDocker.jetson_env_keys"
                            :dialog="showNewDockerOptions"
                            :opts="this.selectedEnvVariableContainerOptionAppJetson?.options"
                            :isValid="this.isEnvValidAppJetson"
                            @update:isValid="isEnvValidAppJetson = $event"
                          ></create-docker-options>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel style="border: 2px solid black" class="mb-2">
                      <v-expansion-panel-header>
                        {{ updatedDocker.docker_name }} Arguments (Jetson)
                        <small style="font-size: 0.7rem; margin-left: 5px">
                          Engine arguments for Jetson architecture.
                        </small>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row class="d-flex justify-end">
                          <v-col cols="4" class="ma-0">
                            <v-autocomplete
                              outlined
                              hide-details
                              v-model="selectedEngineArgContainerOptionAppJetson"
                              :items="containerOptions.filter((item) => item.type === 'arg')"
                              item-text="name"
                              return-object
                              label="Choose Argument Options"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <create-docker-options
                            :dockerOptions="this.updatedDocker.jetson_arguments"
                            :dialog="showNewDockerOptions"
                            :opts="this.selectedEngineArgContainerOptionAppJetson?.options"
                            :isValid="this.isArgValidAppJetson"
                            @update:isValid="isArgValidAppJetson = $event"
                          ></create-docker-options>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-tab-item>

                <!-- Model Tag x86 Settings -->
                <v-tab-item key="model-x86" v-if="hasConfigs">
                  <v-alert v-if="!selectedTag" type="info" text>
                    Please select a model tag to configure x86 settings.
                  </v-alert>
                  <v-expansion-panels v-else>
                    <v-expansion-panel style="border: 2px solid black" class="mb-2">
                      <v-expansion-panel-header>
                        Model Tag Container Options (x86)
                        <small style="font-size: 0.7rem; margin-left: 5px">
                          Docker options for selected model tag on x86.
                        </small>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <create-docker-options
                          :dockerOptions="selectedTag?.docker_options || []"
                          :dialog="showNewDockerOptions"
                          :opts="dockerOpts"
                          :isValid="this.isOptsValidModelX86"
                          @update:isValid="isOptsValidModelX86 = $event"
                        ></create-docker-options>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel style="border: 2px solid black" class="mb-2">
                      <v-expansion-panel-header>
                        Model Tag Environment Variables (x86)
                        <small style="font-size: 0.7rem; margin-left: 5px">
                          Environment variables for selected model tag on x86.
                        </small>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row class="d-flex justify-end">
                          <v-col cols="4" class="ma-0">
                            <v-autocomplete
                              outlined
                              hide-details
                              v-model="selectedEnvVariableContainerOptionModelX86"
                              :items="containerOptions.filter((item) => item.type === 'env')"
                              item-text="name"
                              return-object
                              label="Choose Environment Options"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <create-docker-options
                            :dockerOptions="selectedTag?.environment_variables || []"
                            :dialog="showNewDockerOptions"
                            :opts="this.selectedEnvVariableContainerOptionModelX86?.options"
                            :isValid="this.isEnvValidModelX86"
                            @update:isValid="isEnvValidModelX86 = $event"
                          ></create-docker-options>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel style="border: 2px solid black" class="mb-2">
                      <v-expansion-panel-header>
                        Model Tag Engine Arguments (x86)
                        <small style="font-size: 0.7rem; margin-left: 5px">
                          Engine arguments for selected model tag on x86.
                        </small>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row class="d-flex justify-end">
                          <v-col cols="4" class="ma-0">
                            <v-autocomplete
                              outlined
                              hide-details
                              v-model="selectedEngineArgContainerOptionModelX86"
                              :items="containerOptions.filter((item) => item.type === 'arg')"
                              item-text="name"
                              return-object
                              label="Choose Argument Options"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <create-docker-options
                            :dockerOptions="selectedTag?.engine_arguments || []"
                            :dialog="showNewDockerOptions"
                            :opts="this.selectedEngineArgContainerOptionModelX86?.options"
                            :isValid="this.isArgValidModelX86"
                            @update:isValid="isArgValidModelX86 = $event"
                          ></create-docker-options>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-tab-item>

                <!-- Model Tag Jetson Settings -->
                <v-tab-item key="model-jetson" v-if="hasConfigs">
                  <v-alert v-if="!selectedTag" type="info" text>
                    Please select a model tag to configure Jetson settings.
                  </v-alert>
                  <v-expansion-panels v-else>
                    <v-expansion-panel style="border: 2px solid black" class="mb-2">
                      <v-expansion-panel-header>
                        Model Tag Container Options (Jetson)
                        <small style="font-size: 0.7rem; margin-left: 5px">
                          Docker options for selected model tag on Jetson.
                        </small>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <create-docker-options
                          :dockerOptions="selectedTag?.jetson_docker_options || []"
                          :dialog="showNewDockerOptions"
                          :opts="dockerOpts"
                          :isValid="this.isOptsValidModelJetson"
                          @update:isValid="isOptsValidModelJetson = $event"
                        ></create-docker-options>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel style="border: 2px solid black" class="mb-2">
                      <v-expansion-panel-header>
                        Model Tag Environment Variables (Jetson)
                        <small style="font-size: 0.7rem; margin-left: 5px">
                          Environment variables for selected model tag on Jetson.
                        </small>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row class="d-flex justify-end">
                          <v-col cols="4" class="ma-0">
                            <v-autocomplete
                              outlined
                              hide-details
                              v-model="selectedEnvVariableContainerOptionModelJetson"
                              :items="containerOptions.filter((item) => item.type === 'env')"
                              item-text="name"
                              return-object
                              label="Choose Environment Options"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <create-docker-options
                            :dockerOptions="selectedTag?.jetson_environment_variables || []"
                            :dialog="showNewDockerOptions"
                            :opts="this.selectedEnvVariableContainerOptionModelJetson?.options"
                            :isValid="this.isEnvValidModelJetson"
                            @update:isValid="isEnvValidModelJetson = $event"
                          ></create-docker-options>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel style="border: 2px solid black" class="mb-2">
                      <v-expansion-panel-header>
                        Model Tag Engine Arguments (Jetson)
                        <small style="font-size: 0.7rem; margin-left: 5px">
                          Engine arguments for selected model tag on Jetson.
                        </small>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row class="d-flex justify-end">
                          <v-col cols="4" class="ma-0">
                            <v-autocomplete
                              outlined
                              hide-details
                              v-model="selectedEngineArgContainerOptionModelJetson"
                              :items="containerOptions.filter((item) => item.type === 'arg')"
                              item-text="name"
                              return-object
                              label="Choose Argument Options"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <create-docker-options
                            :dockerOptions="selectedTag?.jetson_engine_arguments || []"
                            :dialog="showNewDockerOptions"
                            :opts="this.selectedEngineArgContainerOptionModelJetson?.options"
                            :isValid="this.isArgValidModelJetson"
                            @update:isValid="isArgValidModelJetson = $event"
                          ></create-docker-options>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-tab-item>
              </v-tabs-items>
            </v-col>

            <v-col cols="12">
              <v-row class="mb-2">
                <v-col cols="12" class="pb-0">
                  <span class="subtitle-2 font-weight-bold">Description Source</span>
                </v-col>
                <v-col cols="12" class="pt-1">
                  <v-radio-group
                    v-model="descSourceRadio"
                    row
                    dense
                    hide-details
                    @change="onDescSourceTypeChange"
                  >
                    <v-radio label="Manual" value="manual"></v-radio>
                    <v-radio label="GitHub README" value="github_readme"></v-radio>
                    <v-radio label="NGC Catalog" value="ngc_info"></v-radio>
                    <v-radio label="Docker Hub" value="dockerhub"></v-radio>
                    <v-radio label="Custom URL" value="raw_url"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>

              <v-row v-if="descSourceRadio !== 'manual'" class="mb-2">
                <v-col cols="6" v-if="descSourceRadio === 'github_readme'">
                  <v-text-field
                    v-model="updatedDocker.desc_source_url"
                    label="GitHub Repository (owner/repo)"
                    outlined
                    dense
                    persistent-hint
                    hint="e.g. vllm-project/vllm"
                    :rules="[v => !!v && v.includes('/') || 'Must be in owner/repo format']"
                  />
                </v-col>
                <v-col cols="3" v-if="descSourceRadio === 'github_readme'">
                  <v-select
                    v-model="githubBranch"
                    :items="['main', 'master', 'develop']"
                    label="Branch"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="6" v-if="descSourceRadio === 'ngc_info'">
                  <v-text-field
                    v-model="updatedDocker.desc_source_url"
                    label="NGC Repository"
                    outlined
                    dense
                    persistent-hint
                    hint="e.g. nvcr.io/nvidia/tensorrt"
                  />
                  <v-btn x-small text color="primary" @click="fillDescSourceUrlFromUrl">
                    Fill from URL
                  </v-btn>
                </v-col>
                <v-col cols="6" v-if="descSourceRadio === 'dockerhub'">
                  <v-text-field
                    v-model="updatedDocker.desc_source_url"
                    label="Docker Hub Repository"
                    outlined
                    dense
                    persistent-hint
                    hint="e.g. ollama/ollama"
                  />
                  <v-btn x-small text color="primary" @click="fillDescSourceUrlFromUrl">
                    Fill from URL
                  </v-btn>
                </v-col>
                <v-col cols="6" v-if="descSourceRadio === 'raw_url'">
                  <v-text-field
                    v-model="updatedDocker.desc_source_url"
                    label="URL"
                    outlined
                    dense
                    persistent-hint
                    hint="e.g. https://example.com/app-desc.md"
                    :rules="[v => !!v && (v.startsWith('http://') || v.startsWith('https://')) || 'Must be a valid URL']"
                  />
                </v-col>
                <v-col cols="6" v-if="updatedDocker.desc_updated_at" class="d-flex align-center">
                  <span class="text-caption">
                    Last updated: {{ formatDate(updatedDocker.desc_updated_at) }}
                  </span>
                </v-col>
              </v-row>

              <v-textarea
                outlined
                label="Description"
                v-model="updatedDocker.desc"
                :readonly="descSourceRadio !== 'manual'"
                :class="descSourceRadio !== 'manual' ? 'grey lighten-4' : ''"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-divider></v-divider>

          <div class="mt-2">
            <docker-tags
              v-if="selectedDockerInfo"
              :selectedDocker="updatedDocker"
              :allJetpacks="allJetpacks"
            ></docker-tags>
            <v-alert text type="info" v-else>
              Create docker before adding a new tag.
            </v-alert>
          </div>
        </v-form>
      </v-card-text>
      <v-card-text class="d-flex justify-end">
        <v-btn text @click="cancel"> Cancel </v-btn>
        <v-btn
          class="ml-5"
          :disabled="!form || loading || !allFormsValid"
          color="primary"
          :loading="loading"
          @click="saveData"
        >
          {{ update ? 'Update' : 'Save' }}
        </v-btn>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { apiService } from '../../service/api.service'
import converter from '../../mixins/converter'
import Swal from 'sweetalert2'
import { VueEditor } from 'vue2-editor'
import CreateDockerEnv from './CreateDockerEnv.vue'
import DockerTags from './DockerTags.vue'
import CreateDockerOptions from './CreateDockerOptions.vue'
import dockerOpts from '../../docker-opts.json'
import ModelTags from './ModelTags.vue'

export default converter.extend({
  components: {
    VueEditor,
    CreateDockerEnv,
    DockerTags,
    CreateDockerOptions,
    ModelTags
  },
  mixins: [converter],
  computed: {
    showCreateDockerView: {
      get () {
        return this.value
      }
    },
    hasChildren () {
      return this.hasConfigs
    },
    // Dropdown tags for bulk-update autocomplete (uses lightweight names endpoint)
    availableTagsForSelection () {
      return this.tagNamesForAutocomplete.map((t) => ({
        id: t.id,
        name: t.tag_name,
        disabled: false
      }))
    },
    // Check if all tags are selected
    isAllSelected () {
      return this.availableTagsForSelection.length > 0 &&
             this.selectedTags.length === this.availableTagsForSelection.length
    },
    // Icon for select all checkbox
    selectAllIcon () {
      if (this.isAllSelected) return 'mdi-checkbox-marked'
      if (this.selectedTags.length > 0) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    // Combined validation for all forms
    allFormsValid () {
      return this.isOptsValidAppX86 && this.isEnvValidAppX86 && this.isArgValidAppX86 &&
             this.isOptsValidAppJetson && this.isEnvValidAppJetson && this.isArgValidAppJetson &&
             this.isOptsValidModelX86 && this.isEnvValidModelX86 && this.isArgValidModelX86 &&
             this.isOptsValidModelJetson && this.isEnvValidModelJetson && this.isArgValidModelJetson
    }
  },
  data () {
    return {
      types: ['app', 'docker', 'compose'],
      rules: [(v) => !!v || 'Field is required'],
      form: true,
      loading: false,
      update: false,
      showNewDockerEnv: false,
      showNewDockerOptions: false,
      showNewArgumentsOptions: false,
      // Configuration tab
      configTab: 0,
      updatedDocker: {
        dockerName: '',
        type: '',
        desc: '',
        short_desc: '',
        labels: [],
        roles: [],
        source: '',
        url: '',
        internal_ports: [],
        models_list: [],
        folder_bind: '',
        env_keys: [],
        docker_options: [],
        arguments: [],
        engine_id: null,
        background_image: null,
        selected_options: [],
        cover_image: null,
        background_is_dark: false,
        cover_is_dark: false,
        check_gpu: false,
        check_compute_capability: false,
        check_jetpack_version: false,
        jetson_env_keys: [],
        jetson_docker_options: [],
        jetson_arguments: [],
        jetson_selected_options: [],
        execute_command: null,
        is_spark: true,
        desc_source_type: null,
        desc_source_url: null,
        desc_updated_at: null
      },
      internalPortsInput: [],
      containerOptions: [],
      // Separate container options for each configuration
      // Application x86
      selectedEnvVariableContainerOptionAppX86: null,
      selectedEngineArgContainerOptionAppX86: null,
      // Application Jetson
      selectedEnvVariableContainerOptionAppJetson: null,
      selectedEngineArgContainerOptionAppJetson: null,
      // Model Tag x86
      selectedEnvVariableContainerOptionModelX86: null,
      selectedEngineArgContainerOptionModelX86: null,
      // Model Tag Jetson
      selectedEnvVariableContainerOptionModelJetson: null,
      selectedEngineArgContainerOptionModelJetson: null,
      dockerOpts: dockerOpts,
      // Separate validation states for each configuration
      // Application x86
      isOptsValidAppX86: true,
      isEnvValidAppX86: true,
      isArgValidAppX86: true,
      // Application Jetson
      isOptsValidAppJetson: true,
      isEnvValidAppJetson: true,
      isArgValidAppJetson: true,
      // Model Tag x86
      isOptsValidModelX86: true,
      isEnvValidModelX86: true,
      isArgValidModelX86: true,
      // Model Tag Jetson
      isOptsValidModelJetson: true,
      isEnvValidModelJetson: true,
      isArgValidModelJetson: true,
      engines: [],
      selectedEngine: null,
      models: [],
      currentModel: null,
      hasConfigs: false,
      tagNamesForAutocomplete: [],
      selectedTag: null,
      selectedModelInfo: null,
      fileRules: [
        (v) => !v || v.size < 500000 || 'File size should be less than 500 KB!'
      ],
      selectedTags: [],
      descSourceRadio: 'manual',
      githubBranch: 'main'
    }
  },
  async mounted () {
    await this.getAllContainerOptions()
    if (this.selectedDockerInfo) {
      this.updatedDocker = { ...this.selectedDockerInfo }
      this.internalPortsInput = this.selectedDockerInfo.internal_ports.map(
        (port) => port.internalPort
      )
      this.update = true
      this.updatedDocker.labels = this.updatedDocker.labels?.split(',')

      if (this.updatedDocker.desc_source_type) {
        this.descSourceRadio = this.updatedDocker.desc_source_type
      } else {
        this.descSourceRadio = 'manual'
      }

      // Load container options for Application x86
      if (
        this.updatedDocker.selected_options?.length > 0 &&
        this.updatedDocker.selected_options[0]?.selectedEnvVariableContainerOption
      ) {
        this.selectedEnvVariableContainerOptionAppX86 = this.containerOptions.find(
          (item) => item.id === this.updatedDocker.selected_options[0].selectedEnvVariableContainerOption
        )
        this.selectedEngineArgContainerOptionAppX86 = this.containerOptions.find(
          (item) => item.id === this.updatedDocker.selected_options[0].selectedEngineArgContainerOption
        )
      }

      // Load container options for Application Jetson
      if (
        this.updatedDocker.jetson_selected_options?.length > 0 &&
        this.updatedDocker.jetson_selected_options[0]?.selectedEnvVariableContainerOption
      ) {
        this.selectedEnvVariableContainerOptionAppJetson = this.containerOptions.find(
          (item) => item.id === this.updatedDocker.jetson_selected_options[0].selectedEnvVariableContainerOption
        )
        this.selectedEngineArgContainerOptionAppJetson = this.containerOptions.find(
          (item) => item.id === this.updatedDocker.jetson_selected_options[0].selectedEngineArgContainerOption
        )
      }
    }
    await this.getEngines()
    this.selectedEngine = this.engines.find(
      (engine) => engine.id === this.updatedDocker.engine_id
    )
    if (this.selectedEngine) {
      await this.getEngineConfigurations(this.selectedEngine.id)
    }
  },
  props: {
    value: Boolean,
    allRoles: Array,
    allSources: Array,
    allDevices: Array,
    allJetpacks: Array,
    selectedDockerInfo: {
      type: Object,
      default: () => null
    }
  },
  methods: {
    saveDockerImage (file, type) {
      if (type === 'background') {
        this.updatedDocker.background_image = file
      } else if (type === 'cover') {
        this.updatedDocker.cover_image = file
      }
    },
    onDescSourceTypeChange (val) {
      if (val === 'manual') {
        this.updatedDocker.desc_source_type = null
        this.updatedDocker.desc_source_url = null
      } else {
        this.updatedDocker.desc_source_type = val
        this.updatedDocker.desc_source_url = ''
      }
    },
    fillDescSourceUrlFromUrl () {
      this.updatedDocker.desc_source_url = this.updatedDocker.url || ''
    },
    formatDate (dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toUTCString().replace('GMT', 'UTC')
    },
    saveData: function () {
      if (this.$refs.newDocker.validate()) {
        this.loading = true
        const updateItems = (items) => {
          items.forEach((item) => {
            if (item.isFromAdmin) item.isFromAdmin = false
          })
        }

        if (
          this.selectedTag &&
          this.selectedTag.environment_variables.length > 0
        ) {
          updateItems(this.selectedTag.environment_variables)
        }
        if (this.selectedTag && this.selectedTag.engine_arguments.length > 0) {
          updateItems(this.selectedTag.engine_arguments)
        }

        if (
          this.selectedTag &&
          this.selectedTag.jetson_environment_variables.length > 0
        ) {
          updateItems(this.selectedTag.jetson_environment_variables)
        }
        if (
          this.selectedTag &&
          this.selectedTag.jetson_engine_arguments.length > 0
        ) {
          updateItems(this.selectedTag.jetson_engine_arguments)
        }
        const formData = new FormData()

        const rawFields = {
          dockerId: this.update ? this.selectedDockerInfo?.id : '',
          dockerName: this.updatedDocker.docker_name,
          type: this.updatedDocker.type,
          desc: this.updatedDocker.desc,
          shortDesc: this.updatedDocker.short_desc,
          labels: JSON.stringify(this.updatedDocker.labels),
          rolesIds: JSON.stringify(this.updatedDocker.roles),
          sourceId: this.updatedDocker.source?.source_name || '',
          url: this.updatedDocker?.url || '',
          internalPorts: JSON.stringify(this.updatedDocker.internal_ports),
          modelsList: JSON.stringify(this.updatedDocker.models_list),
          folderBind: this.updatedDocker.folder_bind || '',
          backgroundIsDark: this.updatedDocker.background_is_dark,
          coverIsDark: this.updatedDocker.cover_is_dark,
          checkGpu: this.updatedDocker.check_gpu,
          checkComputeCapability: this.updatedDocker.check_compute_capability,
          checkJetpackVersion: this.updatedDocker.check_jetpack_version,
          isSpark: this.updatedDocker.is_spark,
          executeCommand: this.updatedDocker.execute_command
            ? JSON.stringify(this.updatedDocker.execute_command)
            : null,
          descSourceType: this.descSourceRadio === 'manual' ? '' : (this.updatedDocker.desc_source_type || ''),
          descSourceUrl: this.updatedDocker.desc_source_url || ''
        }

        const simpleFields = Object.fromEntries(
          Object.entries(rawFields).filter(([_, value]) => {
            return (
              value !== '' &&
              value !== null &&
              value !== undefined &&
              !(typeof value === 'string' && value.trim() === '') // boş string koruması
            )
          })
        )

        for (const [key, value] of Object.entries(simpleFields)) {
          formData.append(key, value ?? '')
        }

        if (this.updatedDocker.background_image instanceof File) {
          formData.append(
            'backgroundImage',
            this.updatedDocker.background_image
          )
        }

        if (this.updatedDocker.cover_image instanceof File) {
          formData.append('coverImage', this.updatedDocker.cover_image)
        }
        if (this.selectedEngine) {
          formData.append('engineId', this.selectedEngine.id)
        }

        // Always save Application x86 and Jetson settings
        const standardConfig = {
          envKeys: JSON.stringify(this.updatedDocker.env_keys || []),
          dockerOptions: JSON.stringify(this.updatedDocker.docker_options || []),
          arguments: JSON.stringify(this.updatedDocker.arguments || []),
          selectedOptions: JSON.stringify([
            {
              selectedEnvVariableContainerOption:
                this.selectedEnvVariableContainerOptionAppX86?.id ?? null,
              selectedEngineArgContainerOption:
                this.selectedEngineArgContainerOptionAppX86?.id ?? null
            }
          ])
        }

        for (const [key, value] of Object.entries(standardConfig)) {
          formData.append(key, value)
        }

        const jetsonConfig = {
          jetsonEnvKeys: JSON.stringify(this.updatedDocker.jetson_env_keys || []),
          jetsonDockerOptions: JSON.stringify(this.updatedDocker.jetson_docker_options || []),
          jetsonArguments: JSON.stringify(this.updatedDocker.jetson_arguments || []),
          jetsonSelectedOptions: JSON.stringify([
            {
              selectedEnvVariableContainerOption:
                this.selectedEnvVariableContainerOptionAppJetson?.id ?? null,
              selectedEngineArgContainerOption:
                this.selectedEngineArgContainerOptionAppJetson?.id ?? null
            }
          ])
        }

        for (const [key, value] of Object.entries(jetsonConfig)) {
          formData.append(key, value)
        }

        apiService
          .post(`admin/docker-${this.update ? 'update' : 'new'}`, formData)
          .then(() => {
            // If a model tag (engine configuration) is selected, update using new endpoint
            if (this.selectedTag) {
              // Prepare config payload with both x86 and Jetson container options
              const configPayload = {
                id: this.selectedTag.id,
                environment_variables: this.selectedTag.environment_variables,
                engine_arguments: this.selectedTag.engine_arguments,
                docker_options: this.selectedTag.docker_options,
                environment_container_options_id: this.selectedEnvVariableContainerOptionModelX86?.id ?? null,
                engine_arguments_container_options_id: this.selectedEngineArgContainerOptionModelX86?.id ?? null,
                jetson_environment_variables: this.selectedTag.jetson_environment_variables,
                jetson_engine_arguments: this.selectedTag.jetson_engine_arguments,
                jetson_docker_options: this.selectedTag.jetson_docker_options,
                jetson_environment_container_options_id: this.selectedEnvVariableContainerOptionModelJetson?.id ?? null,
                jetson_engine_arguments_container_options_id: this.selectedEngineArgContainerOptionModelJetson?.id ?? null
              }

              // Get IDs of selected configs (filter out __all__)
              const selectedConfigIds = this.selectedTags
                .filter((item) => item.id !== '__all__')
                .map((item) => item.id)

              apiService
                .put('/admin/engine-llm-tag-configurations/bulk-update', {
                  selectedConfig: configPayload,
                  selectedConfigIds: selectedConfigIds
                })
                .then((res) => {
                  Swal.fire(
                    `${this.update ? 'Updated' : 'Created'}`,
                    `Docker and engine tag configurations have been ${
                      this.update ? 'updated' : 'created'
                    } successfully.`,
                    'success'
                  )
                })
                .catch((error) => {
                  Swal.fire({
                    title: 'Error!',
                    html: `Engine tag configurations could not be saved:<br>${error.response?.data?.message?.replace(
                      /\n/g,
                      '<br>'
                    ) || 'Unknown error'}`,
                    icon: 'error'
                  })
                })
            } else {
              Swal.fire(
                `${this.update ? 'Updated' : 'Created'}`,
                `Docker has been ${
                  this.update ? 'updated' : 'created'
                } successfully.`,
                'success'
              )
            }
            this.$store.dispatch('getDockers')
            this.cancel()
          })
          .catch((error) => {
            Swal.fire({
              title: 'Error!',
              html: `Docker could not be saved:<br>${error.response?.data?.message?.replace(
                /\n/g,
                '<br>'
              ) || 'Unknown error'}`,
              icon: 'error'
            })
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    // Toggle select all functionality
    toggleSelectAll () {
      if (this.isAllSelected) {
        this.selectedTags = []
      } else {
        this.selectedTags = [...this.availableTagsForSelection]
      }
    },
    cancel () {
      this.$emit('input', false)
    },
    async getAllContainerOptions () {
      await apiService.get('admin/container-options').then((res) => {
        this.containerOptions = res.data.container_options
        this.containerOptions.map((item) => {
          item.options.push({ option: 'Add your own value' })
          item.options = item.options.reverse()
        })
      })
    },
    async getEngines () {
      await apiService.get('admin/engines').then((res) => {
        this.engines = res.data.engines
      })
    },
    async getEngineConfigurations (engineId) {
      if (!engineId) {
        this.hasConfigs = false
        this.tagNamesForAutocomplete = []
        return
      }
      // Fetch only the lightweight names list (id + tag_name) for the autocomplete
      // and to know whether this engine has any configurations at all.
      await apiService
        .get(`admin/engine-llm-tag-configurations/by-engine/${engineId}/names`)
        .then((res) => {
          this.tagNamesForAutocomplete = res.data.names || []
          this.hasConfigs = this.tagNamesForAutocomplete.length > 0
        })
        .catch((error) => {
          console.error('Error fetching engine configuration names:', error)
          this.hasConfigs = false
          this.tagNamesForAutocomplete = []
        })
    }
  },
  watch: {
    selectedEngine: async function (newVal) {
      if (newVal) {
        await this.getEngineConfigurations(newVal.id)
        this.selectedTag = null
        this.selectedTags = []
      } else {
        this.hasConfigs = false
        this.tagNamesForAutocomplete = []
        this.selectedTag = null
        this.selectedTags = []
      }
    },
    // When a model tag is selected, load its container options for both x86 and Jetson
    selectedTag: function (newVal) {
      if (newVal) {
        // Load x86 container options
        this.selectedEnvVariableContainerOptionModelX86 = this.containerOptions.find(
          (item) => item.id === newVal.environmentContainerOptionsId
        )
        this.selectedEngineArgContainerOptionModelX86 = this.containerOptions.find(
          (item) => item.id === newVal.engineArgumentsContainerOptionsId
        )

        // Load Jetson container options
        this.selectedEnvVariableContainerOptionModelJetson = this.containerOptions.find(
          (item) => item.id === newVal.jetsonEnvironmentContainerOptionsId
        )
        this.selectedEngineArgContainerOptionModelJetson = this.containerOptions.find(
          (item) => item.id === newVal.jetsonEngineArgumentsContainerOptionsId
        )
      } else {
        // Reset model tag container options when no tag is selected
        this.selectedEnvVariableContainerOptionModelX86 = null
        this.selectedEngineArgContainerOptionModelX86 = null
        this.selectedEnvVariableContainerOptionModelJetson = null
        this.selectedEngineArgContainerOptionModelJetson = null
      }
    },
    // Watch for model x86 environment container option changes
    selectedEnvVariableContainerOptionModelX86: function (newVal) {
      if (newVal && this.selectedTag) {
        this.selectedTag.environmentContainerOptionsId = newVal.id
      }
    },
    // Watch for model x86 engine arg container option changes
    selectedEngineArgContainerOptionModelX86: function (newVal) {
      if (newVal && this.selectedTag) {
        this.selectedTag.engineArgumentsContainerOptionsId = newVal.id
      }
    },
    // Watch for model Jetson environment container option changes
    selectedEnvVariableContainerOptionModelJetson: function (newVal) {
      if (newVal && this.selectedTag) {
        this.selectedTag.jetsonEnvironmentContainerOptionsId = newVal.id
      }
    },
    // Watch for model Jetson engine arg container option changes
    selectedEngineArgContainerOptionModelJetson: function (newVal) {
      if (newVal && this.selectedTag) {
        this.selectedTag.jetsonEngineArgumentsContainerOptionsId = newVal.id
      }
    }
  }
})
</script>
<style scoped>
.font-bigger {
  font-size: 14px !important;
}

.custom-autocomplete .v-input__control {
  max-height: 70px;
  /* Sabit yükseklik */
  overflow-y: auto;
}
</style>
